import { Rndm } from 'twrk/Rndm.js';
import { Arry } from 'twrk/Arry.js';
import { Ease } from 'twrk/Ease.js';
import { M2 } from 'twrk/M2.js';
import { XY } from 'twrk/V2.js';
import { Shdr, shdr_type } from 'twrk/Shdr.js';
import { Wbgl_mono } from 'twrk/Wbgl_mono.js';
import { wbgl_type } from 'twrk/Wbgl.js';
import { Rapr2, rapr2_revolute_t } from 'twrk/Rapr2.js';

import { twrk_type } from './types.js';
import { rope_make, anchor_world } from './rope.js';
import { sprite_load, sprite_make, sprite_t } from './sprite.js';
import { zoom, pan, camera_update, camera_jump } from './camera.js';
import { drumroll_update, drumroll_release } from './sound.js';
import { asset } from './asset.js';

//the swinger is a llama gripping the rope with a front leg, drawn in its hanging pose. The
//physics chain from the rope down maps the front leg and body onto the classic trapeze figure:
//arm_upper (front foot to knee), arm_lower (knee to shoulder), torso, leg_upper (hip to knee),
//leg_lower (knee to rear foot). Neck and head are two extra links holding the drawn pose, the
//tail flaps freely like the catcher's wing, and the far-side limbs are pure visuals that copy
//the near limbs' rotation.

let _shader: shdr_type | null = null;

function get_shader(Canvas: wbgl_type): shdr_type {
    if (!_shader) {
        _shader = Shdr.make(Canvas, {
            id: "swinger_2d",
            vertex: `#version 300 es
            precision mediump float;
            in vec2 position;
            uniform vec2 u_screen;
            uniform float u_zoom;
            uniform vec2 u_pan;
            void main() {
                gl_Position = vec4((position - u_pan) * u_zoom * u_screen, 0.0, 1.0);
            }
            `,
            fragment: `#version 300 es
            precision mediump float;
            uniform vec4 u_color;
            out vec4 fragColor;
            void main() {
                fragColor = u_color;
            }
            `
        });
    }
    return _shader;
}

//the llama parts are 960x960 layers sharing one frame, drawn hanging by the front leg
const FRAME = 960;
const SRC = {
    torso: asset('llama/torso.png'),
    neck: asset('llama/neck.png'),
    head: asset('llama/head.png'),
    tail: asset('llama/tail.png'),
    arm_front_upper: asset('llama/arm_front_upper.png'),
    arm_front_lower: asset('llama/arm_front_lower.png'),
    arm_back_upper: asset('llama/arm_back_upper.png'),
    arm_back_lower: asset('llama/arm_back_lower.png'),
    leg_front_upper: asset('llama/leg_front_upper.png'),
    leg_front_lower: asset('llama/leg_front_lower.png'),
    leg_back_upper: asset('llama/leg_back_upper.png'),
    leg_back_lower: asset('llama/leg_back_lower.png'),
};

//joint pivots inside the shared frame, image px (y down). The source art faces left but the
//game llama swings to the right, so M mirrors every pivot on store and the sprites render with
//flip on — all math below lives in the mirrored, right-facing frame.
const M = (x: number, y: number): XY => ({ x: FRAME - x, y });
const IMG = {
    hand: M(597, 110), //front foot tip, where the rope grips
    elbow: M(582, 235), //the front knee, playing the trapeze figure's elbow
    shoulder: M(580, 395),
    hip: M(577, 605),
    knee: M(582, 778),
    foot: M(547, 940),
    neck_base: M(530, 365),
    head_joint: M(522, 232),
    nose: M(425, 217),
    tail_root: M(625, 597),
    tail_tip: M(665, 607),
    shoulder_back: M(522, 400),
    elbow_back: M(538, 235),
    hand_back: M(550, 97),
    hip_back: M(505, 600),
    knee_back: M(476, 770),
    foot_back: M(452, 930),
};

export async function swinger_load(Canvas: wbgl_type) {
    await sprite_load(Canvas, Object.values(SRC));
}

type swinger_input = {
    id?: string;
    x?: number; //hand rest position
    y?: number;
    rope?: number;
};

const presets = {
    rope: 2,
    size: 1.3, //world height of the full 960px llama frame
    mass_arm_upper: 0.1,
    mass_arm_lower: 0.1,
    mass_torso: 0.5,
    mass_leg_upper: 0.1,
    mass_leg_lower: 0.2,
    mass_neck: 0.08,
    mass_head: 0.1,
    mass_tail: 0.02,
    swing: 0.5, //initial sideways velocity, world units/s
    joint_stiff: 2, //motor stiffness on all joints, at all times
    tail_stiff: 3, //spring pulling the tail back to its drawn pose; combined with low damping this makes it bounce/oscillate rather than flop
    tail_range: 0.9, //how far the tail may fold away from that pose, either way (twrk rad)
    motor_hipp_r: -Math.PI, //hip motor target on press, radians from hanging straight (~108°); negative folds the legs to the right
    motor_hipp_ms: 300, //how long the fold/unfold takes
    motor_hipp_ease: "cubicInOut",
    motor_knee_r: Math.PI * 0.9, //knee motor target on press, radians from straight leg
    motor_shou_r: -Math.PI * 1, //shoulder motor target on press, radians from hanging straight
    motor_elbo_r: Math.PI * 1, //elbow motor target on press, radians from straight arm
    pump: 0.4, //extra swing velocity added on each motor_hipp/unfold, world units/s
    release_spin: 6, //angular velocity kick (rad/s) given to all bodies when letting go of the rope, so it tumbles nicely
};

const dist = (a: XY, b: XY) => Math.hypot(b.x - a.x, b.y - a.y);
const dir = (a: XY, b: XY) => { const l = dist(a, b); return { x: (b.x - a.x) / l, y: (b.y - a.y) / l }; };
//twrk body angle whose top-anchor axis points along d: (sin a, -cos a) = d, root at anchor {0,+h}
const bone_angle = (d: XY) => Math.atan2(d.x, -d.y);

export function swinger_manager(twrk: twrk_type) {

    const { Canvas, Scrn, Inpt, Rapr, Dbug } = twrk;
    const shader = get_shader(Canvas);

    const list: any[] = [];

    function make(io: swinger_input = {}) {

        const id = io.id || Rndm.id();
        const rope_len = io.rope ?? presets.rope;
        const x = io.x ?? 0;
        const y_hand = io.y ?? -0.75;
        const k = presets.size / FRAME; //world units per frame px

        //bone half-lengths (anchor to anchor), straight from the frame's joint pivots
        const h_au = dist(IMG.hand, IMG.elbow) * k * 0.5;
        const h_al = dist(IMG.elbow, IMG.shoulder) * k * 0.5;
        const h_to = dist(IMG.shoulder, IMG.hip) * k * 0.5;
        const h_lu = dist(IMG.hip, IMG.knee) * k * 0.5;
        const h_ll = dist(IMG.knee, IMG.foot) * k * 0.5;
        const h_ne = dist(IMG.neck_base, IMG.head_joint) * k * 0.5;
        const h_hd = dist(IMG.head_joint, IMG.nose) * k * 0.5;
        const h_tl = dist(IMG.tail_root, IMG.tail_tip) * k * 0.5;

        //llama parts collide with nothing (not even each other), they only hang and swing
        const groups = Rapr2.groups(0b10, 0);

        const make_bone = (pos: XY, angle: number, half: number, radius: number, mass: number) =>
            Rapr.capsule({ pos, angle, half_height: half, radius, mass, groups, velocity_damp: 0.01, angular_damp: 0.01 });

        //the main chain, straight and vertical from the hand, capsules along their local Y
        let yy = y_hand;
        const arm_upper = make_bone({ x, y: yy + h_au }, 0, h_au, 0.02, presets.mass_arm_upper); yy += h_au * 2;
        const arm_lower = make_bone({ x, y: yy + h_al }, 0, h_al, 0.025, presets.mass_arm_lower); yy += h_al * 2;
        const torso = make_bone({ x, y: yy + h_to }, 0, h_to, 0.045, presets.mass_torso);
        const y_torso = yy + h_to; yy += h_to * 2;
        const leg_upper = make_bone({ x, y: yy + h_lu }, 0, h_lu, 0.028, presets.mass_leg_upper); yy += h_lu * 2;
        const leg_lower = make_bone({ x, y: yy + h_ll }, 0, h_ll, 0.023, presets.mass_leg_lower);

        //maps a frame pivot into torso-local coords: the torso capsule runs shoulder→hip along
        //local +y, so points riding on the torso (neck root, tail root, the far-side limb roots)
        //keep their drawn offset from that axis
        const u = dir(IMG.shoulder, IMG.hip);
        const torso_local = (q: XY): XY => {
            const vx = (q.x - IMG.shoulder.x) * k, vy = (q.y - IMG.shoulder.y) * k;
            return { x: vx * u.y - vy * u.x, y: vx * u.x + vy * u.y - h_to };
        };

        //neck and head: two links rooted on the torso at the drawn neck base, spawned at their
        //image angles so the joint motors hold the drawn pose while letting it wobble
        const neck_root_local = torso_local(IMG.neck_base);
        const neck_root = { x: x + neck_root_local.x, y: y_torso + neck_root_local.y };
        const d_ne = dir(IMG.neck_base, IMG.head_joint);
        const a_ne = bone_angle(d_ne);
        const neck = make_bone({ x: neck_root.x + d_ne.x * h_ne, y: neck_root.y + d_ne.y * h_ne }, a_ne, h_ne, 0.03, presets.mass_neck);
        const head_root = { x: neck_root.x + d_ne.x * h_ne * 2, y: neck_root.y + d_ne.y * h_ne * 2 };
        const d_hd = dir(IMG.head_joint, IMG.nose);
        const a_hd = bone_angle(d_hd);
        const head = make_bone({ x: head_root.x + d_hd.x * h_hd, y: head_root.y + d_hd.y * h_hd }, a_hd, h_hd, 0.035, presets.mass_head);

        //tail: rooted at the drawn spot on the torso's rear, springs back to its drawn pose so it
        //bounces/lags behind the swinger's motion instead of hanging limp
        const tail_root_local = torso_local(IMG.tail_root);
        const tail_root = { x: x + tail_root_local.x, y: y_torso + tail_root_local.y };
        const d_tl = dir(IMG.tail_root, IMG.tail_tip);
        const a_tl = bone_angle(d_tl);
        const tail = Rapr.capsule({ pos: { x: tail_root.x + d_tl.x * h_tl, y: tail_root.y + d_tl.y * h_tl }, angle: a_tl, half_height: h_tl, radius: 0.015, mass: presets.mass_tail, groups, velocity_damp: 0.01, angular_damp: 0.001 });

        //rope: hook to hand, keeps swinging under its own inertia once let go of
        const rope = rope_make(twrk, {
            x, y_hook: y_hand - rope_len, len: rope_len, groups,
            grip_body: arm_upper, grip_anchor: { x: 0, y: -h_au },
        });

        //hinges between the limbs, weak motors give a bit of stiffness
        const jnt = presets.joint_stiff;
        const j_elbo = Rapr.revolute({ a: arm_upper, b: arm_lower, anchor_a: { x: 0, y: h_au }, anchor_b: { x: 0, y: -h_al }, limits: [-2.6, 2.6], motor: { stiffness: jnt } });
        const j_shou = Rapr.revolute({ a: arm_lower, b: torso, anchor_a: { x: 0, y: h_al }, anchor_b: { x: 0, y: -h_to }, limits: [-2.6, 2.6], motor: { stiffness: jnt } });
        const j_hipp = Rapr.revolute({ a: torso, b: leg_upper, anchor_a: { x: 0, y: h_to }, anchor_b: { x: 0, y: -h_lu }, limits: [-2.6, 2.6], motor: { stiffness: jnt } });
        const j_knee = Rapr.revolute({ a: leg_upper, b: leg_lower, anchor_a: { x: 0, y: h_lu }, anchor_b: { x: 0, y: -h_ll }, limits: [-2.6, 2.6], motor: { stiffness: jnt } });
        //neck and head motors target the drawn pose instead of a straight chain
        const j_neck = Rapr.revolute({ a: torso, b: neck, anchor_a: neck_root_local, anchor_b: { x: 0, y: h_ne }, limits: [-2.6, 2.6], motor: { stiffness: jnt } });
        j_neck.set_motor(a_ne, jnt);
        const j_head = Rapr.revolute({ a: neck, b: head, anchor_a: { x: 0, y: -h_ne }, anchor_b: { x: 0, y: h_hd }, limits: [-2.6, 2.6], motor: { stiffness: jnt } });
        j_head.set_motor(a_hd - a_ne, jnt);
        const j_tail = Rapr.revolute({ a: torso, b: tail, anchor_a: tail_root_local, anchor_b: { x: 0, y: h_tl }, limits: [a_tl - presets.tail_range, a_tl + presets.tail_range], motor: { stiffness: presets.tail_stiff } });
        j_tail.set_motor(a_tl, presets.tail_stiff);

        const bodies = [arm_upper, arm_lower, torso, leg_upper, leg_lower, neck, head, tail];

        //initial push so the pendulum starts swinging
        for (const b of bodies) b.set_vel({ x: presets.swing, y: 0 });

        //"w"/"e"/"r": motor_hipp! the hip, knee, shoulder and elbow motors each fold their joint while held, release relaxes straight
        let hip_now = 0;
        let knee_now = 0;
        let shou_now = 0;
        let elbo_now = 0;
        const fold_to = (joint: rapr2_revolute_t, id_suffix: string, start: number, aim: number, set: (v: number) => void) => {
            Ease.make({
                id: id + id_suffix,
                start, aim,
                duration: presets.motor_hipp_ms,
                ease: presets.motor_hipp_ease,
                what: (v: number) => {
                    set(v);
                    joint.set_motor(v, presets.joint_stiff);
                },
            });
        };
        //pumping: kick everyone along the current swing direction each time a motor_hipp fires or releases,
        //like a real swinger timing the fold to the pendulum's motion to build up momentum
        const pump = (sign: number) => {
            const dir = Math.sign(torso.vel.x) || sign;
            for (const b of bodies) b.set_vel({ x: b.vel.x + dir * sign * presets.pump, y: b.vel.y });
        };
        const off_key_hip_down = Inpt.on_key("q", () => { fold_to(j_hipp, "_hip", hip_now, presets.motor_hipp_r, v => hip_now = v); pump(1); });
        const off_key_knee_down = Inpt.on_key("w", () => { fold_to(j_knee, "_knee", knee_now, presets.motor_knee_r, v => knee_now = v); pump(1); });
        const off_key_arm_down = Inpt.on_key("e", () => {
            fold_to(j_shou, "_shou", shou_now, presets.motor_shou_r, v => shou_now = v);
            fold_to(j_elbo, "_elbo", elbo_now, presets.motor_elbo_r, v => elbo_now = v);
            pump(1);
        });
        //Inpt.on_key only fires on key-down (edge triggered); release is detected by polling held state each frame
        let q_was_down = false;
        let w_was_down = false;
        let e_was_down = false;
        const poll_keys = () => {
            const q_down = !!Inpt.keys["q"];
            if (q_was_down && !q_down) { fold_to(j_hipp, "_hip", hip_now, 0, v => hip_now = v); pump(1); }
            q_was_down = q_down;
            const w_down = !!Inpt.keys["w"];
            if (w_was_down && !w_down) { fold_to(j_knee, "_knee", knee_now, 0, v => knee_now = v); pump(1); }
            w_was_down = w_down;
            const e_down = !!Inpt.keys["e"];
            if (e_was_down && !e_down) {
                fold_to(j_shou, "_shou", shou_now, 0, v => shou_now = v);
                fold_to(j_elbo, "_elbo", elbo_now, 0, v => elbo_now = v);
                pump(1);
            }
            e_was_down = e_down;
        };

        //space: let go of the rope. Only the joint linking the rope's last segment to the arm dies —
        //the rope itself is a real body chain, so it keeps swinging under its own inertia afterwards.
        //also gives a spin: a little angular kick to every body so the swinger tumbles nicely once free.
        let released = false;
        //tumble kick shared by every kind of letting-go: the rope on space, and later a catcher's unhold
        const kick = () => {
            const dir = Math.sign(torso.vel.x) || 1;
            for (const b of bodies) b.set_ang_vel(b.ang_vel + dir * presets.release_spin);
        };

        //snapshot of every physics body (llama chain + rope segments/hook) taken at the moment of
        //release, so a botched drop can be replayed from right before letting go instead of from scratch
        const snap_bodies = [...bodies, rope.hook, ...rope.segs];
        type snapshot_t = { pos: XY; angle: number; vel: XY; ang_vel: number };
        let release_snapshot: snapshot_t[] | null = null;
        const take_snapshot = () => {
            release_snapshot = snap_bodies.map(b => ({ pos: { x: b.pos.x, y: b.pos.y }, angle: b.angle, vel: { x: b.vel.x, y: b.vel.y }, ang_vel: b.ang_vel }));
        };
        const restore_snapshot = () => {
            if (!release_snapshot) return;
            for (let i = 0; i < snap_bodies.length; i++) {
                const b = snap_bodies[i], s = release_snapshot[i];
                b.set_pos(s.pos);
                b.set_angle(s.angle);
                b.set_vel(s.vel);
                b.set_ang_vel(s.ang_vel);
            }
        };

        const off_key_release = Inpt.on_key(" ", () => {
            if (released) return;
            released = true;
            take_snapshot();
            rope.release();
            kick();
            camera_jump();
            drumroll_release();
        });

        //far-side limbs: no bodies of their own — each frame they render rooted on the torso at
        //their drawn spots and copy the matching near limb's bone directions, pure visuals
        const sho_back_local = torso_local(IMG.shoulder_back);
        const hip_back_local = torso_local(IMG.hip_back);
        const l_abu = dist(IMG.shoulder_back, IMG.elbow_back) * k;
        const l_abl = dist(IMG.elbow_back, IMG.hand_back) * k;
        const l_lbu = dist(IMG.hip_back, IMG.knee_back) * k;
        const l_lbl = dist(IMG.knee_back, IMG.foot_back) * k;
        const along = (a: XY, b: XY, root: XY, len: number): XY => {
            const l = dist(a, b) || 1;
            return { x: root.x + (b.x - a.x) / l * len, y: root.y + (b.y - a.y) / l * len };
        };
        const arm_back_pts = (): [XY, XY, XY] => {
            const root = anchor_world(torso, sho_back_local);
            const elbow = along(anchor_world(arm_lower, { x: 0, y: h_al }), anchor_world(arm_lower, { x: 0, y: -h_al }), root, l_abu);
            const hand = along(anchor_world(arm_upper, { x: 0, y: h_au }), anchor_world(arm_upper, { x: 0, y: -h_au }), elbow, l_abl);
            return [root, elbow, hand];
        };
        const leg_back_pts = (): [XY, XY, XY] => {
            const root = anchor_world(torso, hip_back_local);
            const knee = along(anchor_world(leg_upper, { x: 0, y: -h_lu }), anchor_world(leg_upper, { x: 0, y: h_lu }), root, l_lbu);
            const foot = along(anchor_world(leg_lower, { x: 0, y: -h_ll }), anchor_world(leg_lower, { x: 0, y: h_ll }), knee, l_lbl);
            return [root, knee, foot];
        };

        //graphics: each part is a frame-cut sprite pinned to two moving points, listed back to
        //front — far-side limbs behind everything, then tail, neck, head, torso (its saddle covers
        //the neck base), and the near legs and gripping foreleg on top
        const parts: { gfx: sprite_t; ends: () => [XY, XY] }[] = [];
        const make_part = (src: string, p1: XY, p2: XY, ends: () => [XY, XY]) =>
            parts.push({ gfx: sprite_make(Canvas, Scrn, { src, frame: FRAME, size: presets.size, p1, p2, flip: true }), ends });

        make_part(SRC.arm_back_lower, IMG.elbow_back, IMG.hand_back, () => { const [, e, h] = arm_back_pts(); return [e, h]; });
        make_part(SRC.arm_back_upper, IMG.shoulder_back, IMG.elbow_back, () => { const [r, e] = arm_back_pts(); return [r, e]; });
        make_part(SRC.leg_back_lower, IMG.knee_back, IMG.foot_back, () => { const [, kn, ft] = leg_back_pts(); return [kn, ft]; });
        make_part(SRC.leg_back_upper, IMG.hip_back, IMG.knee_back, () => { const [r, kn] = leg_back_pts(); return [r, kn]; });
        make_part(SRC.tail, IMG.tail_root, IMG.tail_tip, () => [anchor_world(tail, { x: 0, y: h_tl }), anchor_world(tail, { x: 0, y: -h_tl })]);
        make_part(SRC.neck, IMG.neck_base, IMG.head_joint, () => [anchor_world(neck, { x: 0, y: h_ne }), anchor_world(neck, { x: 0, y: -h_ne })]);
        make_part(SRC.head, IMG.head_joint, IMG.nose, () => [anchor_world(head, { x: 0, y: h_hd }), anchor_world(head, { x: 0, y: -h_hd })]);
        make_part(SRC.torso, IMG.shoulder, IMG.hip, () => [anchor_world(torso, { x: 0, y: -h_to }), anchor_world(torso, { x: 0, y: h_to })]);
        make_part(SRC.leg_front_upper, IMG.hip, IMG.knee, () => [anchor_world(leg_upper, { x: 0, y: -h_lu }), anchor_world(leg_upper, { x: 0, y: h_lu })]);
        make_part(SRC.leg_front_lower, IMG.knee, IMG.foot, () => [anchor_world(leg_lower, { x: 0, y: -h_ll }), anchor_world(leg_lower, { x: 0, y: h_ll })]);
        make_part(SRC.arm_front_upper, IMG.elbow, IMG.shoulder, () => [anchor_world(arm_lower, { x: 0, y: -h_al }), anchor_world(arm_lower, { x: 0, y: h_al })]);
        make_part(SRC.arm_front_lower, IMG.hand, IMG.elbow, () => [anchor_world(arm_upper, { x: 0, y: -h_au }), anchor_world(arm_upper, { x: 0, y: h_au })]);

        //debug: a blue quad at every joint pivot, computed from the same twrk-local anchors the
        //physics joints use, so a quad in the wrong place means the joint really is wired wrong
        const joint_size = 0.03;
        const joint_ends: (() => XY)[] = [
            () => anchor_world(arm_upper, { x: 0, y: -h_au }), //rope grip (hand)
            () => anchor_world(arm_upper, { x: 0, y: h_au }), //elbow
            () => anchor_world(arm_lower, { x: 0, y: h_al }), //shoulder
            () => anchor_world(torso, { x: 0, y: h_to }), //hip
            () => anchor_world(leg_upper, { x: 0, y: h_lu }), //knee
            () => anchor_world(leg_lower, { x: 0, y: h_ll }), //foot
            () => anchor_world(torso, neck_root_local), //neck root
            () => anchor_world(neck, { x: 0, y: -h_ne }), //head joint
            () => anchor_world(torso, tail_root_local), //tail root
        ];
        const joint_gfx = joint_ends.map(ends => {
            const mesh = M2.shape.rect({ width: joint_size, height: joint_size });
            const gl = Wbgl_mono.make(Canvas, shader, Scrn, { mesh, uniforms: { u_color: [0.2, 0.4, 1, 1], u_zoom: 1, u_pan: [0, 0] } });
            return { ends, mesh, gl };
        });

        //the hand, same twrk-local anchor the rope grips: what the catcher latches onto after release
        const hand_anchor = { x: 0, y: -h_au };

        const f = {
            id,
            hook: rope.hook, arm_upper, arm_lower, torso, leg_upper, leg_lower,
            hand: () => anchor_world(arm_upper, hand_anchor),
            hand_anchor,
            is_released: () => released,
            //detach the q/w/e/space controls without tearing down the body — used at the finale so
            //the frozen llama stops responding to input
            unbind_keys: () => { off_key_hip_down(); off_key_knee_down(); off_key_arm_down(); off_key_release(); },
            kick,
            take_snapshot,
            //redo: replay the whole body chain back to its last take_snapshot(). Only re-grips this
            //rope if it's still the station currently hanging free (main.ts decides that — if a
            //catcher further down the chain is the current free end, this swinger's own rope stays
            //released, same as any earlier catcher's rope).
            redo: (re_grip: boolean) => {
                restore_snapshot();
                if (re_grip) { rope.re_grip(); released = false; }
                for (const b of bodies) b.sync_read();
                rope.sync_read();
            },
            fell: () => torso.pos.y > 5,
            run: function () {

                poll_keys();

                for (const b of bodies) b.sync_read();
                rope.sync_read();

                for (const p of parts) {
                    const [a, b] = p.ends();
                    p.gfx.render(a, b, zoom.value, pan);
                }

                if (Dbug.on) for (const j of joint_gfx) {
                    const p = j.ends();
                    M2.shape.rect({ width: joint_size, height: joint_size, vertices: j.mesh });
                    for (let i = 0; i < j.mesh.length; i += 2) {
                        j.mesh[i] += p.x;
                        j.mesh[i + 1] += p.y;
                    }
                    j.gl.mesh_update(j.mesh);
                    j.gl.uniforms.u_zoom = zoom.value;
                    j.gl.uniforms.u_pan = [pan.x, pan.y];
                    j.gl.render();
                }

                //rope drawn last so its curve renders on top of the llama/joint debug graphics
                rope.render(zoom.value, pan);
            },
            kill: function () {
                off_key_hip_down();
                off_key_knee_down();
                off_key_arm_down();
                off_key_release();
                Ease.kill(id + "_hip");
                Ease.kill(id + "_knee");
                Ease.kill(id + "_shou");
                Ease.kill(id + "_elbo");
                for (const p of parts) p.gfx.kill();
                for (const j of joint_gfx) j.gl.kill();
                rope.kill();
                for (const j of [j_elbo, j_shou, j_hipp, j_knee, j_neck, j_head, j_tail]) j.kill();
                for (const b of bodies) b.kill();
                Arry.remove.id(list, id);
            },
        };

        list.push(f);
        return f;
    }

    //finale: detach input and stop re-rolling the drum so the show holds still under the end title
    let finished = false;
    return {
        list,
        make,
        get: (id: string) => Arry.get.id(list, id),
        kill: (id: string) => { const s = Arry.get.id(list, id); if (s) s.kill(); },
        finish: function () { finished = true; for (const s of list) s.unbind_keys(); },
        run: function () {
            for (const s of list) s.run();
            const target = list[0];
            if (target) {
                const speed = Math.hypot(target.torso.vel.x, target.torso.vel.y);
                camera_update(speed);
                if (!finished) drumroll_update(speed); //no fresh drum rolls after the show ends
            }
        },
    };
}
